/**
 * CSV 取り込みダイアログ：ファイル選択 → ヘッダ + プレビュー → email/name 列指定 → 取り込み実行。
 * 仕様書: docs/01_spec/spec_mail_system.md B-10.3
 */
import { useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { FileUp, Layers, Upload, UserPlus, X } from 'lucide-react';
import Papa from 'papaparse';
import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import { ColumnMergePanel } from './ColumnMergePanel';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useMailContacts } from '@/hooks/useMailContacts';
import { functions } from '@/lib/firebase';

const EMAIL_COLUMN_HINTS = [
  'email',
  'e-mail',
  'mail',
  'email address',
  'mail address',
  'メールアドレス',
  'メール',
  'メアド',
];

const NAME_COLUMN_HINTS = ['name', 'fullname', 'full name', '氏名', '名前', '担当者', '担当'];

/**
 * デフォルト (構造化) フィールドのキーと、その自動検出ヒント (列名のサブ文字列または完全一致)。
 * 値は CSV/xlsx のヘッダ列名にマッピングされる。
 */
type DefaultFieldKey =
  | 'lastName'
  | 'firstName'
  | 'fullName'
  | 'lastNameKana'
  | 'firstNameKana'
  | 'company'
  | 'phone'
  | 'occupation'
  | 'gender'
  | 'birthday'
  | 'postalCode'
  | 'prefecture'
  | 'city'
  | 'addressLine';

const DEFAULT_FIELD_DEFS: Record<
  DefaultFieldKey,
  { label: string; hints: string[] }
> = {
  lastName: {
    label: '姓',
    hints: ['姓', '苗字', 'last name', 'lastname', 'surname', 'family name'],
  },
  firstName: {
    label: '名',
    hints: ['名', 'first name', 'firstname', 'given name'],
  },
  fullName: {
    label: '氏名 (姓/名がない場合のフォールバック)',
    hints: ['氏名', '名前', 'full name', 'fullname'],
  },
  lastNameKana: {
    label: '姓 (カナ)',
    hints: ['姓（カナ）', '姓(カナ)', 'セイ', 'カナ姓', 'last name kana'],
  },
  firstNameKana: {
    label: '名 (メイ)',
    hints: ['名（メイ）', '名（カナ）', '名(カナ)', 'メイ', 'カナ名', 'first name kana'],
  },
  company: {
    label: '会社名',
    hints: ['会社名', '会社', 'company', '法人名', '組織名'],
  },
  phone: {
    label: '電話番号',
    hints: ['電話番号', '電話', 'tel', 'phone', '携帯', '携帯番号', 'mobile'],
  },
  occupation: {
    label: '職業',
    hints: ['職業', 'occupation', 'job', '職種'],
  },
  gender: {
    label: '性別',
    hints: ['性別', 'gender', 'sex'],
  },
  birthday: {
    label: '誕生日 / 生年月日',
    hints: ['生年月日', '誕生日', 'birthday', 'birth date', 'date of birth', 'dob'],
  },
  postalCode: {
    label: '郵便番号',
    hints: ['郵便番号', '郵便', '〒', 'postal', 'postal code', 'zip', 'zip code'],
  },
  prefecture: {
    label: '都道府県',
    hints: ['都道府県', '県', 'prefecture'],
  },
  city: {
    label: '市区町村',
    hints: ['市区町村', 'city'],
  },
  addressLine: {
    label: '町名・番地',
    hints: ['町名・番地', '町名', '番地', '住所', 'address', 'street'],
  },
};

interface ImportSummary {
  imported: number;
  merged: number;
  skipped: number;
}

type DefaultFieldMapping = Partial<Record<DefaultFieldKey, string | null>>;

const importContactsFromCsvFn = httpsCallable<
  {
    rows: Array<Record<string, string>>;
    emailKey: string;
    nameKey?: string;
    importTitles?: string[];
    promoteToCustomer?: boolean;
    defaultFieldMapping?: DefaultFieldMapping;
  },
  { ok: boolean; summary: ImportSummary }
>(functions, 'importContactsFromCsv');

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ParsedCsv {
  headers: string[];
  rows: Array<Record<string, string>>;
  fileName: string;
}

function autoDetectColumn(headers: string[], hints: string[]): string {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const hint of hints) {
    const idx = lower.indexOf(hint.toLowerCase());
    if (idx >= 0) return headers[idx];
  }
  // 部分一致フォールバック
  for (const hint of hints) {
    const idx = lower.findIndex((h) => h.includes(hint.toLowerCase()));
    if (idx >= 0) return headers[idx];
  }
  return '';
}

type DialogTab = 'import' | 'merge';

export function ContactImportDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<DialogTab>('import');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [emailKey, setEmailKey] = useState('');
  const [nameKey, setNameKey] = useState('');
  const [importTitles, setImportTitles] = useState<string[]>([]);
  const [titleInput, setTitleInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [promoteToCustomer, setPromoteToCustomer] = useState(false);

  /**
   * カラムごとの取込 ON/OFF。
   * email/name 用に選ばれた列を除く全列が「カスタムフィールド」として取込対象。
   * 初期値は全 ON。「全解除」ボタンで全 OFF、「全選択」で全 ON。
   */
  const [enabledColumns, setEnabledColumns] = useState<Record<string, boolean>>(
    {},
  );

  /**
   * デフォルトフィールド (姓/名/会社/電話 等) の列マッピング。自動検出済み。
   * UI 上でユーザーが列名を再選択できる。マッピングされた列は customFields からは除外される。
   */
  const [defaultFieldMapping, setDefaultFieldMapping] =
    useState<DefaultFieldMapping>({});

  // 既存タグ一覧（importTitles のサジェスト用）
  const { data: existingContacts } = useMailContacts({ limit: 500 });
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const c of existingContacts ?? []) {
      for (const t of c.tags ?? []) {
        if (t.trim()) set.add(t);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [existingContacts]);

  const filteredTags = useMemo(() => {
    const k = titleInput.trim().toLowerCase();
    if (!k) return allTags;
    return allTags.filter((t) => t.toLowerCase().includes(k));
  }, [allTags, titleInput]);

  /** 1 個または複数（区切り文字含む文字列）を chip 化 */
  const addImportTitle = (raw: string) => {
    // 全角/半角カンマ・タブ・改行で複数同時追加にも対応
    const parts = raw
      .split(/[,、\t\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    setImportTitles((prev) => {
      const next = [...prev];
      for (const p of parts) {
        if (!next.includes(p)) next.push(p);
      }
      return next;
    });
    setTitleInput('');
  };

  const toggleImportTitle = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    setImportTitles((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const removeImportTitle = (t: string) => {
    setImportTitles((prev) => prev.filter((x) => x !== t));
  };

  const reset = () => {
    setParsed(null);
    setEmailKey('');
    setNameKey('');
    setImportTitles([]);
    setTitleInput('');
    setSummary(null);
    setPromoteToCustomer(false);
    setEnabledColumns({});
    setDefaultFieldMapping({});
    setTab('import');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  /** 解析結果を共通形式で受け取って state を更新 */
  const applyParsed = (
    headers: string[],
    rows: Array<Record<string, string>>,
    fileName: string,
  ) => {
    if (headers.length === 0 || rows.length === 0) {
      toast.error('有効なヘッダ／データ行が見つかりません');
      return;
    }
    setParsed({ headers, rows, fileName });
    const detectedEmail = autoDetectColumn(headers, EMAIL_COLUMN_HINTS);
    const detectedName = autoDetectColumn(headers, NAME_COLUMN_HINTS);
    setEmailKey(detectedEmail);
    setNameKey(detectedName);
    // デフォルトフィールドの自動マッピング
    const dfm: DefaultFieldMapping = {};
    for (const key of Object.keys(DEFAULT_FIELD_DEFS) as DefaultFieldKey[]) {
      const found = autoDetectColumn(headers, DEFAULT_FIELD_DEFS[key].hints);
      dfm[key] = found || null;
    }
    setDefaultFieldMapping(dfm);
    // すべての列をデフォルト ON で初期化
    const init: Record<string, boolean> = {};
    for (const h of headers) init[h] = true;
    setEnabledColumns(init);
  };

  /** xlsx/xls を最初のシートだけ読み、ヘッダ+データの object 配列に正規化 */
  const parseXlsx = async (file: File) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) {
      toast.error('シートが見つかりません');
      return;
    }
    const sheet = wb.Sheets[sheetName];
    // header:1 で 2D 配列として読み、最初の行をヘッダ扱い
    const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      blankrows: false,
    });
    if (raw.length < 2) {
      toast.error('データ行がありません (ヘッダ + 1 行以上必要)');
      return;
    }
    const headerRow = (raw[0] as unknown[]).map((c) => String(c ?? '').trim());
    // ヘッダ重複時は連番でユニーク化
    const seen = new Map<string, number>();
    const headers = headerRow.map((h) => {
      if (!h) return '__col_' + seen.size;
      const n = seen.get(h) ?? 0;
      seen.set(h, n + 1);
      return n === 0 ? h : `${h}_${n + 1}`;
    });
    const toStr = (v: unknown): string => {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'boolean') return v ? 'true' : 'false';
      if (v instanceof Date) return v.toISOString();
      return String(v);
    };
    const rows: Array<Record<string, string>> = [];
    for (let i = 1; i < raw.length; i++) {
      const r = raw[i] as unknown[];
      const obj: Record<string, string> = {};
      let hasValue = false;
      for (let j = 0; j < headers.length; j++) {
        const val = toStr(r?.[j]);
        if (val !== '') hasValue = true;
        obj[headers[j]] = val;
      }
      if (hasValue) rows.push(obj);
    }
    applyParsed(headers, rows, file.name);
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setSummary(null);
    const name = file.name.toLowerCase();
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xls');
    try {
      if (isXlsx) {
        await parseXlsx(file);
        setParsing(false);
        return;
      }
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setParsing(false);
          if (result.errors.length > 0) {
            toast.error(`CSV パース失敗: ${result.errors[0].message}`);
            return;
          }
          const headers = result.meta.fields ?? [];
          const rows = result.data.filter(
            (r): r is Record<string, string> =>
              r !== null && typeof r === 'object',
          );
          applyParsed(headers, rows, file.name);
        },
        error: (err) => {
          setParsing(false);
          toast.error(`CSV 読み込み失敗: ${err.message}`);
        },
      });
    } catch (err) {
      setParsing(false);
      toast.error(
        `ファイル読み込み失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleSubmit = async () => {
    if (!parsed || !emailKey) return;
    // 確定し忘れた入力欄の文字も自動でタグ化
    const trailing = titleInput.trim();
    const finalTitles = trailing && !importTitles.includes(trailing)
      ? [...importTitles, trailing]
      : importTitles;

    // デフォルトフィールド対象の列キー集合
    const defaultFieldColumns = new Set<string>(
      Object.values(defaultFieldMapping).filter(
        (v): v is string => typeof v === 'string' && v.length > 0,
      ),
    );

    // 取込対象列だけに絞った行を構築
    // - email/name 列は常に含める
    // - デフォルトフィールド対象列は常に含める (チェックボックスから外れていても)
    // - その他は enabledColumns でフィルタ
    const includedKeys = new Set<string>();
    if (emailKey) includedKeys.add(emailKey);
    if (nameKey) includedKeys.add(nameKey);
    for (const c of defaultFieldColumns) includedKeys.add(c);
    for (const h of parsed.headers) {
      if (enabledColumns[h]) includedKeys.add(h);
    }
    const filteredRows = parsed.rows.map((row) => {
      const out: Record<string, string> = {};
      for (const k of includedKeys) {
        if (k in row) out[k] = row[k];
      }
      return out;
    });

    setSubmitting(true);
    try {
      const res = await importContactsFromCsvFn({
        rows: filteredRows,
        emailKey,
        nameKey: nameKey || undefined,
        importTitles: finalTitles.length > 0 ? finalTitles : undefined,
        promoteToCustomer: promoteToCustomer || undefined,
        defaultFieldMapping: Object.keys(defaultFieldMapping).length > 0
          ? defaultFieldMapping
          : undefined,
      });
      setSummary(res.data.summary);
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
      toast.success(
        `取り込み完了: 新規 ${res.data.summary.imported} / マージ ${res.data.summary.merged} / スキップ ${res.data.summary.skipped}`,
      );
    } catch (err) {
      toast.error(`取り込み失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const previewRows = parsed?.rows.slice(0, 5) ?? [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>CSV / Excel からデータを取り込み</DialogTitle>
          <DialogDescription>
            {tab === 'import'
              ? 'CSV / Excel から新規配信先を登録 + 既存配信先にマージします。'
              : 'CSV / Excel の指定カラムを既存配信先の指定フィールドに統合します (新規作成はしません)。'}
          </DialogDescription>
        </DialogHeader>

        {/* タブ */}
        <div className="flex items-center gap-1 border-b border-[var(--border)]">
          {(
            [
              {
                key: 'import' as const,
                label: '新規取込 + マージ',
                Icon: UserPlus,
              },
              {
                key: 'merge' as const,
                label: 'カラム統合 (差分マージ)',
                Icon: Layers,
              },
            ]
          ).map((t) => {
            const TabIcon = t.Icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-xs border-b-2 transition-colors flex items-center gap-1.5 ${
                  tab === t.key
                    ? 'border-[var(--accent)] text-[var(--accent)] font-medium'
                    : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                <TabIcon className="size-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'merge' && (
          <ColumnMergePanel onDone={() => onOpenChange(false)} />
        )}

        {tab === 'import' && !parsed && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-[var(--border)] rounded-lg p-12 text-center hover:border-[var(--accent)] transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                const n = file?.name.toLowerCase() ?? '';
                if (
                  file &&
                  (n.endsWith('.csv') || n.endsWith('.xlsx') || n.endsWith('.xls'))
                ) {
                  handleFile(file);
                } else {
                  toast.error('.csv / .xlsx / .xls ファイルをドロップしてください');
                }
              }}
            >
              <FileUp className="size-10 mx-auto text-[var(--text-muted)] mb-3" />
              <div className="text-sm text-[var(--text-secondary)]">
                クリックまたはドラッグ＆ドロップで CSV / Excel を選択
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-1">
                .csv / .xlsx / .xls 対応。先頭行をヘッダ行として扱います
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFileChange}
              className="hidden"
            />
            {parsing && (
              <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                <Spinner className="size-3" /> ファイルをパース中…
              </div>
            )}
          </div>
        )}

        {tab === 'import' && parsed && !summary && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
              <FileUp className="size-3.5" />
              <span className="font-medium text-[var(--text-secondary)]">{parsed.fileName}</span>
              <span>· {parsed.rows.length} 行 / {parsed.headers.length} 列</span>
              <button
                type="button"
                className="ml-auto text-[var(--accent)] hover:underline"
                onClick={reset}
              >
                別のファイルを選択
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">email カラム（必須）</Label>
                <Select value={emailKey} onValueChange={setEmailKey}>
                  <SelectTrigger>
                    <SelectValue placeholder="メアド列を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {parsed.headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">name カラム（任意）</Label>
                <Select value={nameKey || '__none__'} onValueChange={(v) => setNameKey(v === '__none__' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="名前列を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">（指定なし）</SelectItem>
                    {parsed.headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">
                インポートタイトル（任意 — タグとして付与されます。Enter で確定、複数追加可）
              </Label>
              <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.25rem]">
                {importTitles.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeImportTitle(t)}
                      className="hover:text-[var(--accent-red)] -mr-0.5"
                      aria-label={`${t} を削除`}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={
                    importTitles.length === 0
                      ? '例: 2026年4月キャンペーン申込者（Enter / カンマ で確定）'
                      : '追加タグを入力'
                  }
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  onKeyDown={(e) => {
                    // IME 変換中の Enter は変換確定に消費されるため、ここでは無視
                    const composing =
                      e.nativeEvent.isComposing || e.keyCode === 229;
                    if (e.key === 'Enter' && !composing) {
                      e.preventDefault();
                      addImportTitle(titleInput);
                    } else if (
                      e.key === 'Backspace' &&
                      titleInput === '' &&
                      importTitles.length > 0 &&
                      !composing
                    ) {
                      // 入力欄が空 + Backspace → 末尾タグを削除
                      removeImportTitle(importTitles[importTitles.length - 1]);
                    }
                  }}
                  onBlur={() => {
                    // フォーカスが外れた瞬間に確定（IME 環境のフォールバック）
                    if (titleInput.trim()) addImportTitle(titleInput);
                  }}
                  className="flex-1 min-w-[12ch] bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                />
              </div>
              {titleInput.trim() && (
                <button
                  type="button"
                  onClick={() => addImportTitle(titleInput)}
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[var(--accent)] hover:underline"
                >
                  + 「{titleInput.trim()}」をタグとして追加
                </button>
              )}
              {allTags.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] text-[var(--text-muted)] mb-1">
                    既存タグから選択{titleInput.trim() ? `（${filteredTags.length} / ${allTags.length} 件一致）` : `（全 ${allTags.length} 件）`}
                  </div>
                  {filteredTags.length === 0 ? (
                    <div className="text-[11px] text-[var(--text-muted)] italic">
                      一致する既存タグなし — Enter で新しいタグとして追加されます
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {filteredTags.map((t) => {
                        const selected = importTitles.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => toggleImportTitle(t)}
                            className={
                              'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border transition-colors ' +
                              (selected
                                ? 'bg-[var(--accent-subtle)] text-[var(--accent)] border-[var(--accent)]'
                                : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] border-transparent hover:border-[var(--accent)] hover:text-[var(--accent)]')
                            }
                            title={selected ? 'クリックで解除' : 'クリックで追加'}
                          >
                            {selected && <span className="mr-1">✓</span>}
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)]">
              <div className="flex-1 min-w-0">
                <Label className="text-xs font-semibold">顧客として取り込む</Label>
                <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                  オンにすると、新規行は{' '}
                  <code className="text-[10px]">promotionLevel=&quot;customer&quot;</code>{' '}
                  （顧客昇格状態）で登録されます。
                  メアド/氏名でマッチした既存オーディエンスは
                  <strong className="text-[var(--text-secondary)]"> lead のみ </strong>
                  自動昇格し、customer / inactive はそのまま据え置きです。
                </p>
              </div>
              <Switch
                checked={promoteToCustomer}
                onChange={(e) => setPromoteToCustomer(e.target.checked)}
              />
            </div>

            {/* デフォルトフィールド (構造化フィールドへの自動マッピング) */}
            <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] p-3">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-semibold">
                      デフォルトフィールド (自動マッピング)
                    </Label>
                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]">
                      常時取込
                    </span>
                  </div>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    姓 / 名 / 会社名 / 電話番号 / 職業 / 住所 / 性別 / 誕生日 等は構造化フィールドとして保存され、検索・表示で優先利用されます。
                    下の「全選択 / 全解除」の影響は受けません。列名は自動検出されますが、必要に応じて変更できます。
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {(Object.keys(DEFAULT_FIELD_DEFS) as DefaultFieldKey[]).map(
                  (key) => {
                    const def = DEFAULT_FIELD_DEFS[key];
                    const current = defaultFieldMapping[key] ?? '';
                    return (
                      <label
                        key={key}
                        className="flex items-center gap-2 text-xs"
                      >
                        <span
                          className="w-32 shrink-0 text-[var(--text-secondary)] truncate"
                          title={def.label}
                        >
                          {def.label}
                        </span>
                        <select
                          value={current}
                          onChange={(e) =>
                            setDefaultFieldMapping((prev) => ({
                              ...prev,
                              [key]: e.target.value || null,
                            }))
                          }
                          className="flex-1 px-2 py-1 text-xs rounded bg-[var(--bg-input)] border border-[var(--border)] min-w-0"
                        >
                          <option value="">— 取込まない —</option>
                          {parsed.headers.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  },
                )}
              </div>
            </div>

            {/* 取込列の選択 (email/name + デフォルトフィールド対象列を除外) */}
            <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-xs font-semibold">
                    カスタムフィールドとして取込む列
                  </Label>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    チェックされた列のみ各配信先の customFields に保存されます。
                    email / name と上のデフォルトフィールド対象列は除外されます。
                    <span className="ml-1 text-[var(--text-secondary)]">
                      全選択 / 全解除 はこのカスタム列のみが対象 (デフォルトフィールドには影響しません)。
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-[var(--text-muted)]">
                    {
                      parsed.headers.filter(
                        (h) =>
                          h !== emailKey &&
                          h !== nameKey &&
                          !Object.values(defaultFieldMapping).includes(h) &&
                          enabledColumns[h],
                      ).length
                    }
                    /
                    {
                      parsed.headers.filter(
                        (h) =>
                          h !== emailKey &&
                          h !== nameKey &&
                          !Object.values(defaultFieldMapping).includes(h),
                      ).length
                    }{' '}
                    選択
                  </span>
                  <button
                    type="button"
                    className="text-[var(--accent)] hover:underline"
                    title="カスタムフィールドのみ全選択 (デフォルトフィールドには影響しません)"
                    onClick={() => {
                      // email/name とデフォルトフィールド対象列は触らない
                      const reserved = new Set<string>([
                        emailKey,
                        nameKey,
                        ...Object.values(defaultFieldMapping).filter(
                          (v): v is string => typeof v === 'string' && v.length > 0,
                        ),
                      ]);
                      setEnabledColumns((prev) => {
                        const next = { ...prev };
                        for (const h of parsed.headers) {
                          if (!reserved.has(h)) next[h] = true;
                        }
                        return next;
                      });
                    }}
                  >
                    全選択
                  </button>
                  <span className="text-[var(--text-muted)]">/</span>
                  <button
                    type="button"
                    className="text-[var(--accent)] hover:underline"
                    title="カスタムフィールドのみ全解除 (デフォルトフィールドには影響しません)"
                    onClick={() => {
                      const reserved = new Set<string>([
                        emailKey,
                        nameKey,
                        ...Object.values(defaultFieldMapping).filter(
                          (v): v is string => typeof v === 'string' && v.length > 0,
                        ),
                      ]);
                      setEnabledColumns((prev) => {
                        const next = { ...prev };
                        for (const h of parsed.headers) {
                          if (!reserved.has(h)) next[h] = false;
                        }
                        return next;
                      });
                    }}
                  >
                    全解除
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-48 overflow-y-auto pr-1">
                {parsed.headers
                  .filter(
                    (h) =>
                      h !== emailKey &&
                      h !== nameKey &&
                      !Object.values(defaultFieldMapping).includes(h),
                  )
                  .map((h) => (
                    <label
                      key={h}
                      className="flex items-center gap-2 text-xs px-1.5 py-1 rounded cursor-pointer hover:bg-[var(--bg-hover)]"
                    >
                      <input
                        type="checkbox"
                        checked={!!enabledColumns[h]}
                        onChange={(e) =>
                          setEnabledColumns((prev) => ({
                            ...prev,
                            [h]: e.target.checked,
                          }))
                        }
                      />
                      <span className="truncate flex-1">{h}</span>
                    </label>
                  ))}
              </div>
            </div>

            <div>
              <div className="text-xs text-[var(--text-muted)] mb-1.5">
                プレビュー（先頭 5 行）
              </div>
              <div className="border border-[var(--border)] rounded overflow-x-auto max-h-64">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--bg-hover)]">
                    <tr>
                      {parsed.headers.map((h) => {
                        const isEmail = h === emailKey;
                        const isName = h === nameKey;
                        const included =
                          isEmail || isName || !!enabledColumns[h];
                        return (
                          <th
                            key={h}
                            className={`text-left px-2 py-1.5 font-semibold whitespace-nowrap ${
                              isEmail
                                ? 'text-[var(--accent)]'
                                : isName
                                  ? 'text-[var(--accent-purple)]'
                                  : included
                                    ? 'text-[var(--text-secondary)]'
                                    : 'text-[var(--text-muted)] line-through opacity-50'
                            }`}
                          >
                            {h}
                            {isEmail && ' (email)'}
                            {isName && ' (name)'}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} className="border-t border-[var(--border)]">
                        {parsed.headers.map((h) => {
                          const isEmail = h === emailKey;
                          const isName = h === nameKey;
                          const included =
                            isEmail || isName || !!enabledColumns[h];
                          return (
                            <td
                              key={h}
                              className={`px-2 py-1.5 whitespace-nowrap font-mono ${
                                included ? '' : 'opacity-30'
                              }`}
                            >
                              {r[h] ?? ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === 'import' && summary && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 rounded border border-[var(--border)] bg-[var(--bg-elevated)] text-center">
                <div className="text-2xl font-bold text-[var(--accent-green)]">
                  {summary.imported}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">新規作成</div>
              </div>
              <div className="p-4 rounded border border-[var(--border)] bg-[var(--bg-elevated)] text-center">
                <div className="text-2xl font-bold text-[var(--accent)]">
                  {summary.merged}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">既存にマージ</div>
              </div>
              <div className="p-4 rounded border border-[var(--border)] bg-[var(--bg-elevated)] text-center">
                <div className="text-2xl font-bold text-[var(--text-muted)]">
                  {summary.skipped}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">スキップ</div>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              スキップ行はメアド形式が不正、または email カラムが空でした。
            </p>
          </div>
        )}

        {tab === 'import' && (
          <DialogFooter>
            {summary ? (
              <Button type="button" variant="primary" onClick={() => handleClose(false)}>
                閉じる
              </Button>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
                  キャンセル
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!parsed || !emailKey || submitting}
                >
                  {submitting ? <Spinner className="size-3 mr-1" /> : <Upload className="size-3.5 mr-1" />}
                  取り込み実行
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
